

import { useState, useEffect, useMemo } from "react";
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  Briefcase
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { withAuth } from "@/app/components/withAuth";

// Types
interface VendorType {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  vendors_count?: number;
  created_at?: string;
}

// Reusable Action Button Component
function ActionButton({ 
  onClick, 
  icon: Icon, 
  label, 
  variant = 'default' 
}: { 
  onClick: () => void; 
  icon: React.ElementType; 
  label: string;
  variant?: 'default' | 'primary' | 'danger' | 'success';
}) {
  const colors = {
    default: 'text-stone-600 hover:bg-stone-100',
    primary: 'text-amber-600 hover:bg-amber-50',
    danger: 'text-red-600 hover:bg-red-50',
    success: 'text-emerald-600 hover:bg-emerald-50',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Vendor Type Form Component
function VendorTypeForm({ 
  initialData, 
  onSubmit, 
  onCancel,
}: { 
  initialData?: Partial<VendorType>; 
  onSubmit: (data: { name: string; description: string; icon: string }) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    icon: initialData?.icon || "",
  });

  // Reset form when initialData changes (for new entries)
  useEffect(() => {
    if (!initialData?.id) {
      setFormData({
        name: "",
        description: "",
        icon: "",
      });
    } else {
      setFormData({
        name: initialData.name || "",
        description: initialData.description || "",
        icon: initialData.icon || "",
      });
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      description: formData.description,
      icon: formData.icon,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Type Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter vendor type name (e.g., Catering, Photography)"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of this vendor type"
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Icon (optional)</label>
        <input
          type="text"
          value={formData.icon}
          onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
          placeholder="Icon name or emoji (e.g., 🍽️ or Utensils)"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update" : "Add"} Vendor Type
        </button>
      </div>
    </form>
  );
}


function VendorTypesPage() {
  const { showToast } = useToast();
  
  const [vendorTypes, setVendorTypes] = useState<VendorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<VendorType | null>(null);
  
  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingType, setDeletingType] = useState<VendorType | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await mastersApi.vendorTypes.list();
      setVendorTypes(response.data?.data || response.data || []);
    } catch (error) {
      showToast("Failed to load vendor types", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredTypes = useMemo(() => {
    if (!searchQuery) return vendorTypes;
    const q = searchQuery.toLowerCase();
    return vendorTypes.filter(t => 
      t.name.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q)
    );
  }, [vendorTypes, searchQuery]);

  const openDeleteModal = (type: VendorType) => {
    if (type.vendors_count && type.vendors_count > 0) {
      showToast("Cannot delete vendor type with existing vendors", "error");
      return;
    }
    setDeletingType(type);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    
    setDeleteLoading(true);
    try {
      await mastersApi.vendorTypes.delete(deletingType.id);
      showToast("Vendor type deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingType(null);
      loadData(); // Reload data to ensure consistency
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete vendor type";
      showToast(errorMessage, "error");
      setDeleteModalOpen(false);
      setDeletingType(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: { name: string; description: string; icon: string }) => {
    try {
      if (editingType) {
        // Update existing
        await mastersApi.vendorTypes.update(editingType.id, data);
        showToast("Vendor type updated successfully", "success");
      } else {
        // Create new
        await mastersApi.vendorTypes.create(data);
        showToast("Vendor type created successfully", "success");
      }
      
      // Close modal and reset form
      setIsModalOpen(false);
      setEditingType(null);
      loadData(); // Reload to get fresh data
    } catch (error) {
      showToast("Failed to save vendor type", "error");
      loadData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Vendor Types</h1>
        <p className="text-sm text-stone-500">Manage vendor categories and classifications</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search vendor types..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => { setEditingType(null); setIsModalOpen(true); }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Type
        </button>
      </div>

      {/* Content */}
      {filteredTypes.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No vendor types found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Description</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Vendors</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-lg">
                          {type.icon || "🏢"}
                        </div>
                        <span className="font-medium text-stone-800">{type.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">{type.description || '-'}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <span className={`${type.vendors_count ? 'text-amber-700 font-medium' : 'text-stone-400'}`}>
                        {type.vendors_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <ActionButton 
                          onClick={() => { setEditingType(type); setIsModalOpen(true); }} 
                          icon={Pencil}
                          label="Edit"
                          variant="default"
                        />
                        <ActionButton 
                          onClick={() => openDeleteModal(type)} 
                          icon={Trash2}
                          label="Delete" 
                          variant="danger"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingType(null); }}
        title={editingType ? "Edit Vendor Type" : "Add New Vendor Type"}
        size="md"
      >
        <VendorTypeForm
          initialData={editingType || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingType(null); }}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingType(null); }}
        onConfirm={handleDelete}
        title="Delete Vendor Type"
        message="Are you sure you want to delete this vendor type? This action cannot be undone."
        itemName={deletingType?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}

export default withAuth(VendorTypesPage, { allowedRoles: ['admin', 'super_admin', 'manager'] });
